// src/services/menu.service.ts
import { updateDoc, getDocs, getDoc, collection, doc, setDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { COLLECTIONS } from "../constants/collections";
import { resolveItemPhotoUrls, resolveMenuImageUrl } from "../utils/menuImageUrl";

const COLLECTION_NAME = COLLECTIONS.MENUS;
const KITCHEN_COLLECTION_NAME = COLLECTIONS.KITCHENS;

// Helper function to filter active customizations recursively
const filterActiveCustomizations = (levels?: any[]): any[] => {
  if (!levels || levels.length === 0) return [];
  
  return levels
    .filter(level => level.isActive !== false) // Filter active levels
    .map(level => ({
      ...level,
      options: level.options
        .filter((opt: any) => opt.isActive !== false) // Filter active options
        .map((opt: any) => ({
          ...opt,
          customizations: filterActiveCustomizations(opt.customizations) // Recursive filter
        }))
    }))
    .filter(level => level.options.length > 0); // Remove empty levels
};

// (A) Get ALL menu items (flattened) for public explore page, filtering by kitchen status
export const getAllMenuItems = async (): Promise<any[]> => {
  try {
    // Step 1: Fetch all kitchens and build a Map with kitchen ID -> kitchen data
    const kitchenSnapshot = await getDocs(collection(db, KITCHEN_COLLECTION_NAME));
    const kitchenMap = new Map<string, any>();
    
    kitchenSnapshot.docs.forEach(docSnap => {
      const kitchen = docSnap.data();
      kitchenMap.set(docSnap.id, {
        id: docSnap.id,
        name: kitchen.name || "Unnamed Kitchen",
        status: kitchen.status,
        subscriptionStatus: kitchen.subscriptionStatus,
        ...kitchen
      });
    });

    // Step 2: Filter for only open + active kitchens
    const activeOpenKitchens = new Map(
      Array.from(kitchenMap.entries()).filter(([_, kitchen]) => 
        (kitchen.status === "open" || kitchen.status === "active") && kitchen.subscriptionStatus === "active"
      )
    );

    console.log(`✅ Found ${activeOpenKitchens.size} active/open kitchens:`, 
      Array.from(activeOpenKitchens.values()).map(k => `${k.name} (${k.id})`)
    );

    // Step 3: Fetch menu items for active kitchens
    const menuSnapshot = await getDocs(collection(db, COLLECTION_NAME));
    let allItems: any[] = [];
    
    menuSnapshot.docs.forEach(docSnap => {
      const kitchenId = docSnap.id;
      const kitchen = activeOpenKitchens.get(kitchenId);
      
      if (!kitchen) {
        // console.log(`⏭️ Skipping menu for kitchen ${kitchenId} - not active/open`);
        return;
      }

      const menuData = docSnap.data();
      const menuItemsObj = menuData.menuItems || {};
      
      console.log(`📝 Processing ${Object.keys(menuItemsObj).length} items from kitchen: ${kitchen.name} (${kitchenId})`);
      
      Object.entries(menuItemsObj).forEach(([itemName, itemObj]) => {
        const item = itemObj as any;
        
        // ✅ FILTER 1: Skip items marked as unavailable
        if (item.isAvailable === false) {
          return;
        }
        
        const variants = item.Variants || item.Sizes || [];
        const firstVariant = variants[0] || {};

        // ✅ FILTER 2: Filter active customizations only
        const activeCustomizations = filterActiveCustomizations(item.customizations);

        const resolvedItem = resolveItemPhotoUrls({
          ...item,
          customizations: activeCustomizations,
        });
        allItems.push({
          id: `${kitchenId}_${itemName}`,
          name: itemName || item.Base || item.name || "Unknown",
          description: item.Base || item.description || "",
          price: firstVariant.price ?? item.basePrice ?? item.price ?? 0,
          chefName: kitchen.chefName ?? menuData.chefName ?? "",
          photoUrl: resolveMenuImageUrl(item.photoUrl ?? item.imageUrl ?? "") || "",
          kitchenId: kitchenId,
          kitchenName: kitchen.name, // ✅ Use kitchen name from /kitchens collection
          ...resolvedItem,
          customizations: resolvedItem.customizations ?? activeCustomizations,
        });
      });
    });
    
    console.log(`✅ Loaded ${allItems.length} active menu items for customers`);
    return allItems;
  } catch (error) {
    console.error("❌ getAllMenuItems error:", error);
    throw error;
  }
};

// (B) Get menu items for a specific kitchen (from /menus/{kitchenId})
export const getChefMenu = async (kitchenId: string): Promise<any[]> => {
  try {
    console.log("📖 Fetching menu for kitchen:", kitchenId);
    
    // Use the Strict ID passed in
    const menuRef = doc(db, COLLECTION_NAME, kitchenId);
    const menuSnap = await getDoc(menuRef);
    
    if (!menuSnap.exists()) {
      console.warn("⚠️ No menu document found for kitchen:", kitchenId);
      return [];
    }
    
    const menuData = menuSnap.data();
    const menuItemsObj = menuData.menuItems || {};
    
    console.log(`✅ Found ${Object.keys(menuItemsObj).length} items in menu for kitchen ${kitchenId}`);
    
    // Convert nested object to array for display
    return Object.entries(menuItemsObj).map(([itemName, itemObj]) => ({
      id: itemName,
      name: itemName,
      kitchenId: kitchenId,
      ...(itemObj as any),
    }));
  } catch (error) {
    console.error("❌ getChefMenu error:", error);
    throw error;
  }
};

// (C) Add/Update a menu item inside a group (menu doc)
export const addMenuItem = async (
  args: { groupId: string; itemName: string; itemObj: any }
): Promise<void> => {
  try {
    const { groupId, itemName, itemObj } = args;
    
    // STRICT MODE: We assume 'groupId' is already the correct Kitchen ID (e.g., "eggstacy-kitchen")
    const targetId = groupId; 
    
    console.log(`➕ Adding item to menu ID: ${targetId} | Item: ${itemName}`);
    
    const groupRef = doc(db, COLLECTION_NAME, targetId);
    const groupSnap = await getDoc(groupRef);
    
    const existingMenuItems = groupSnap.exists() ? (groupSnap.data().menuItems || {}) : {};
    
    const updatedMenuItems = {
      ...existingMenuItems,
      [itemName]: {
        ...itemObj,
        kitchenId: targetId, // Ensure internal consistency
        createdAt: itemObj.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    };
    
    if (groupSnap.exists()) {
      await updateDoc(groupRef, { 
        menuItems: updatedMenuItems,
        updatedAt: new Date().toISOString(),
      });
    } else {
      // Create the menu document if it doesn't exist
      await setDoc(groupRef, {
        name: targetId, // Fallback name
        kitchenId: targetId,
        menuItems: updatedMenuItems,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }
    
    console.log(`✅ Successfully added/updated "${itemName}"`);
  } catch (error: any) {
    console.error("❌ addMenuItem error:", error);
    throw error;
  }
};

export const updateMenuItem = addMenuItem;

export const deleteMenuItem = async (item: any): Promise<void> => {
  try {
    const kitchenId = item.kitchenId;
    const itemName = item.name || item.id;
    
    console.log("🗑️ Attempting to delete:", { kitchenId, itemName });
    
    if (!kitchenId || !itemName) {
      throw new Error("Kitchen ID or Item Name is missing");
    }
    
    const menuRef = doc(db, COLLECTION_NAME, kitchenId);
    const menuSnap = await getDoc(menuRef);
    
    if (!menuSnap.exists()) {
      throw new Error(`Menu document not found for kitchen: ${kitchenId}`);
    }
    
    const menuData = menuSnap.data();
    const menuItemsObj = menuData.menuItems || {};
    
    if (!(itemName in menuItemsObj)) {
      throw new Error(`Item "${itemName}" not found in menu`);
    }
    
    // Create new object without the deleted item
    const updatedMenuItems = { ...menuItemsObj };
    delete updatedMenuItems[itemName];
    
    await updateDoc(menuRef, { 
      menuItems: updatedMenuItems,
      updatedAt: new Date().toISOString(),
    });
    
    console.log(`✅ Successfully deleted "${itemName}" from kitchen ${kitchenId}`);
  } catch (error: any) {
    console.error("❌ deleteMenuItem error:", error);
    throw error;
  }
};

// Helper: Get kitchen details by ID
export const getKitchenById = async (kitchenId: string) => {
  try {
    const kitchenRef = doc(db, KITCHEN_COLLECTION_NAME, kitchenId);
    const kitchenSnap = await getDoc(kitchenRef);
    
    if (kitchenSnap.exists()) {
      return { id: kitchenSnap.id, ...kitchenSnap.data() };
    }
    return null;
  } catch (error) {
    console.error("Error fetching kitchen:", error);
    return null;
  }
};